import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { createQueryClient } from "@/lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { 
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageQueryBridge } from "@/components/LanguageQueryBridge";
import './i18n/index'; // Initialize i18n
import LaunchPage from "./pages/LaunchPage";
import Index from "./pages/Index";
import Proto1Page from "./pages/Proto1Page";
import QuestionPage from "./pages/QuestionPage";
import TagPage from "./pages/TagPage";
import PersonPage from "./pages/PersonPage";
import InterviewRequestsPage from "./pages/InterviewRequestsPage";
import InterviewSubmissionPage from "./pages/InterviewSubmissionPage";
import TestSubmissionPage from "./pages/TestSubmissionPage";
import InterviewSuccess from "./pages/InterviewSuccess";
import InterviewManagementPage from "./pages/InterviewManagementPage";
import FeederPage from "./pages/FeederPage";
import PublishHistoryPage from "./pages/PublishHistoryPage";
import HowToUsePage from "./pages/HowToUsePage";
import AboutPage from "./pages/AboutPage";
import EmailTemplatePage from "./pages/EmailTemplatePage";
import LoginPage from "./pages/LoginPage";
import AuthPage from "./pages/AuthPage";
import ProductionPage from "./pages/ProductionPage";
import BookingPage from "./pages/BookingPage";
import EmailLogsPage from "./pages/EmailLogsPage";
import AiDashboardPage from "./pages/AiDashboardPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import UserActivityPage from "./pages/UserActivityPage";
import ABTestingPage from "./pages/ABTestingPage";
import AlertsPage from "./pages/AlertsPage";
import BackupPage from "./pages/BackupPage";
import EnterprisePage from "./pages/EnterprisePage";
import AdEconomyPage from "./pages/enterprise/AdEconomyPage";
import MonitoringPage from "./pages/MonitoringPage";
import EditorialPage from "./pages/EditorialPage";
import PublishingQueuePage from "./pages/PublishingQueuePage";
import UserManagementPage from "./pages/UserManagementPage";
import TestGoogleAuthPage from "./pages/TestGoogleAuthPage";
import TestArticles from "./pages/TestArticles";
import GeminiTestPage from "./pages/GeminiTestPage";
import ArticlePage from "./pages/ArticlePage";
import EditorialReviewPage from "./pages/EditorialReviewPage";
import AMAPage from "./pages/AMAPage";
import AMADetailPage from "./pages/AMADetailPage";
import AMAAnswerPage from "./pages/AMAAnswerPage";
import AMAManagement from "./pages/editorial/AMAManagement";
import AMAForm from "./pages/editorial/AMAForm";
import AMAModeration from "./pages/editorial/AMAModeration";
import EditorialApprovalPage from "./pages/EditorialApprovalPage";
import RagMetricsPage from "./pages/RagMetricsPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import { useActivityIntegration } from "./hooks/analytics/useActivityIntegration";
import { useTTSWarmup } from "./hooks/speech/useTTSWarmup";

const queryClient = createQueryClient();

// Component to initialize activity tracking and TTS warmup
const ActivityTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useActivityIntegration();
  useTTSWarmup(); // Warm up TTS on app start
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageQueryBridge />
    <TooltipProvider>
      <AuthProvider>
        <Router>
          <ActivityTrackingProvider>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/launch" element={<LaunchPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/proto1" element={<Proto1Page />} />
            <Route path="/index" element={<Index />} />
            <Route path="/article/:id" element={<ArticlePage />} />
            <Route path="/question/:question" element={<QuestionPage />} />
            <Route path="/tag/:tag" element={<TagPage />} />
            <Route path="/person/:name" element={<PersonPage />} />
            <Route path="/interview-requests" element={<ProtectedRoute><InterviewRequestsPage /></ProtectedRoute>} />
             <Route path="/interview-submission/:requestLink" element={<InterviewSubmissionPage />} />
             <Route path="/interview-submission" element={<InterviewSubmissionPage />} />
            <Route path="/interview-success" element={<InterviewSuccess />} />
            <Route path="/interview-management" element={<AdminRoute><InterviewManagementPage /></AdminRoute>} />
            <Route path="/feeder" element={<AdminRoute><FeederPage /></AdminRoute>} />
            <Route path="/publish-history" element={<AdminRoute><PublishHistoryPage /></AdminRoute>} />
            <Route path="/how-to-use" element={<HowToUsePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/email-templates" element={<AdminRoute><EmailTemplatePage /></AdminRoute>} />
            <Route path="/editorial" element={<AdminRoute><EditorialPage /></AdminRoute>} />
            <Route path="/publishing-queue" element={<AdminRoute><PublishingQueuePage /></AdminRoute>} />
            <Route path="/production" element={<AdminRoute><ProductionPage /></AdminRoute>} />
            <Route path="/booking" element={<AdminRoute><BookingPage /></AdminRoute>} />
            <Route path="/email-logs" element={<AdminRoute><EmailLogsPage /></AdminRoute>} />
            <Route path="/ai-dashboard" element={<ProtectedRoute><AiDashboardPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<AdminRoute><AnalyticsPage /></AdminRoute>} />
            <Route path="/analytics/activity" element={<AdminRoute><UserActivityPage /></AdminRoute>} />
            <Route path="/ab-testing" element={<AdminRoute><ABTestingPage /></AdminRoute>} />
            <Route path="/enterprise" element={<AdminRoute><EnterprisePage /></AdminRoute>} />
            <Route path="/enterprise/ad-economy" element={<AdminRoute><AdEconomyPage /></AdminRoute>} />
            <Route path="/alerts" element={<AdminRoute><AlertsPage /></AdminRoute>} />
            <Route path="/backup" element={<AdminRoute><BackupPage /></AdminRoute>} />
            <Route path="/monitoring" element={<AdminRoute><MonitoringPage /></AdminRoute>} />
            <Route path="/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            <Route path="/test-google-auth" element={<TestGoogleAuthPage />} />
            <Route path="/test-articles" element={<TestArticles />} />
            <Route path="/gemini-test" element={<GeminiTestPage />} />
            <Route path="/ama" element={<AMAPage />} />
            <Route path="/ama/:id" element={<AMADetailPage />} />
            <Route path="/ama/answer/:token" element={<AMAAnswerPage />} />
            <Route path="/editorial/ama" element={<AdminRoute><AMAManagement /></AdminRoute>} />
            <Route path="/editorial/ama/new" element={<AdminRoute><AMAForm /></AdminRoute>} />
            <Route path="/editorial/ama/:id/edit" element={<AdminRoute><AMAForm /></AdminRoute>} />
            <Route path="/editorial/ama/:id" element={<AdminRoute><AMAModeration /></AdminRoute>} />
            <Route path="/editorial-review" element={<AdminRoute><EditorialReviewPage /></AdminRoute>} />
            <Route path="/editorial-approval" element={<AdminRoute><EditorialApprovalPage /></AdminRoute>} />
            <Route path="/rag-metrics" element={<AdminRoute><RagMetricsPage /></AdminRoute>} />
            </Routes>
          </ActivityTrackingProvider>
        </Router>
      </AuthProvider>
    </TooltipProvider>
    <Toaster />
    <Sonner />
  </QueryClientProvider>
);

export default App;
